import { isNil, snakeCase } from "lodash";
import React, { ChangeEvent, useEffect, useState } from "react";

import Col from "react-bootstrap/Col";
import FormControl from "react-bootstrap/FormControl";
import FormSelect from "react-bootstrap/FormSelect";
import Row from "react-bootstrap/Row";

const {
  threat_name_platform: PLATFORMS = [],
  threat_name_subplatform: SUBPLATFORMS = {},
  threat_name_type: THREAT_NAME_TYPE = []
} = window?.tcbase?.consts || {};

type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

interface Props {
  id?: string;
  defaultValue?: string;
  onChange: (
    event: ChangeEvent<FormControlElement | HTMLSelectElement>
  ) => void;
}

const PLATFORM_NAMES = PLATFORMS?.reduce((prev: string[], curr: string) => {
  const platformSubplatforms = SUBPLATFORMS?.[curr] || [];
  if (platformSubplatforms.length) {
    prev.push(...platformSubplatforms.map((sub: string) => `${curr}-${sub}`));
  } else {
    prev.push(curr);
  }
  return prev;
}, []);

const toInternalValue = (threatName?: string) => {
  const [platform, type, family] = threatName?.split(".") ?? [];
  return { platform, type, family };
};

const ThreatName: React.FC<Props> = ({ id = "", defaultValue, onChange }) => {
  const [threatName, setThreatName] = useState(defaultValue ?? "");

  useEffect(() => {
    if (isNil(defaultValue)) return;
    setThreatName(defaultValue);
  }, [defaultValue]);

  const toRepresentation = (newValue: string, part: string): string => {
    const [platform, type, family] = threatName.split(".");
    if (part === "platform") {
      return `${newValue}.${type}.${family}`;
    } else if (part === "type") {
      return `${platform}.${newValue}.${family}`;
    } else {
      return `${platform}.${type}.${newValue}`;
    }
  };

  const createOnChangeEvent = (
    event: ChangeEvent<FormControlElement | HTMLSelectElement>
  ) => {
    const newValue = toRepresentation(event.target.value, event.target.name);

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: newValue
      }
    };

    return newEvent;
  };

  const handleChange = (
    event: ChangeEvent<FormControlElement | HTMLSelectElement>
  ) => {
    const modifiedEvent = createOnChangeEvent(event);
    onChange(modifiedEvent);
  };

  const internalValue = toInternalValue(threatName);

  return (
    <Row>
      <Col xs={4}>
        <FormSelect
          id={`${id}--threat-name-platform`}
          name="platform"
          onChange={handleChange}
          value={internalValue.platform || ""}
          data-testid="threat_name_select_platform"
        >
          <option disabled value="">
            Platform
          </option>
          {PLATFORM_NAMES?.map((name: string) => (
            <option
              id={`${id}--threat-name-platform--${name}`}
              value={name}
              key={name}
              data-testid={`platform_option_${snakeCase(name)}`}
            >
              {name}
            </option>
          ))}
        </FormSelect>
      </Col>
      <Col xs={4}>
        <FormSelect
          id={`${id}--threat-name-type`}
          name="type"
          onChange={handleChange}
          value={internalValue.type || ""}
          data-testid="threat_name_select_threat_type"
        >
          <option disabled value="">
            Type
          </option>
          {THREAT_NAME_TYPE?.map((name: string) => (
            <option
              id={`${id}--threat-name-type--${name}`}
              value={name}
              key={name}
              data-testid={`threat_name_option_${snakeCase(name)}`}
            >
              {name}
            </option>
          ))}
        </FormSelect>
      </Col>
      <Col xs={4}>
        <FormControl
          id={`${id}--threat--name-family`}
          name="family"
          as="input"
          placeholder="Family name"
          onChange={handleChange}
          value={internalValue.family || ""}
          data-testid="threat_name_threat_family"
        />
      </Col>
    </Row>
  );
};

export default ThreatName;
